/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { filter, map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';

import { BusinessType } from '../models/business-type';
import { ClientResponse } from '../models/client-response';
import { CreateClientRequest } from '../models/create-client-request';
import { GenericResponse } from '../models/generic-response';
import { GetClientResponse } from '../models/get-client-response';
import { UpdateClientRequest } from '../models/update-client-request';

/**
 * Client APIs
 */
@Injectable({ providedIn: 'root' })
export class ClientService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getAllClients()` */
  static readonly GetAllClientsPath = '/api/v1/clients';

  /**
   * Get list of clients.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getAllClients()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllClients$Response(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       *  - name
       *  - vatNumber
       *  - crNumber
       *  - additionalNumber
       *  - postalCode
       *  - country
       *  - city
       *  - district
       *  - streetName
       */
      search?: string;

      /**
       * This allows filtering clients by their business type
       */
      clientByBusinessType?: Array<BusinessType>;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GetClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientService.GetAllClientsPath,
      'get'
    );
    if (params) {
      rb.query('page', params.page, {});
      rb.query('size', params.size, {});
      rb.query('sort', params.sort, {});
      rb.query('search', params.search, {});
      rb.query('clientByBusinessType', params.clientByBusinessType, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GetClientResponse>;
        })
      );
  }

  /**
   * Get list of clients.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getAllClients$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getAllClients(
    params?: {
      /**
       * the page number.
       */
      page?: number;

      /**
       * the size of a page
       */
      size?: number;

      /**
       * sort
       */
      sort?: string;

      /**
       * This adds a string search to the following fields.
       *  - name
       *  - vatNumber
       *  - crNumber
       *  - additionalNumber
       *  - postalCode
       *  - country
       *  - city
       *  - district
       *  - streetName
       */
      search?: string;

      /**
       * This allows filtering clients by their business type
       */
      clientByBusinessType?: Array<BusinessType>;
    },
    context?: HttpContext
  ): Observable<GetClientResponse> {
    return this.getAllClients$Response(params, context).pipe(
      map(
        (r: StrictHttpResponse<GetClientResponse>): GetClientResponse => r.body
      )
    );
  }

  /** Path part for operation `createClient()` */
  static readonly CreateClientPath = '/api/v1/clients';

  /**
   * Create new Client.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createClient()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClient$Response(
    params: {
      body: CreateClientRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientService.CreateClientPath,
      'post'
    );
    if (params) {
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClientResponse>;
        })
      );
  }

  /**
   * Create new Client.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createClient$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createClient(
    params: {
      body: CreateClientRequest;
    },
    context?: HttpContext
  ): Observable<ClientResponse> {
    return this.createClient$Response(params, context).pipe(
      map((r: StrictHttpResponse<ClientResponse>): ClientResponse => r.body)
    );
  }

  /** Path part for operation `getClientById()` */
  static readonly GetClientByIdPath = '/api/v1/clients/{id}';

  /**
   * Get client details using id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getClientById()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientById$Response(
    params: {
      /**
       * The client's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientService.GetClientByIdPath,
      'get'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClientResponse>;
        })
      );
  }

  /**
   * Get client details using id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getClientById$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getClientById(
    params: {
      /**
       * The client's id that needs to be fetched
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<ClientResponse> {
    return this.getClientById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ClientResponse>): ClientResponse => r.body)
    );
  }

  /** Path part for operation `disableClient()` */
  static readonly DisableClientPath = '/api/v1/clients/{id}';

  /**
   * disable clients.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `disableClient()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableClient$Response(
    params: {
      /**
       * The client's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<GenericResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientService.DisableClientPath,
      'delete'
    );
    if (params) {
      rb.path('id', params.id, {});
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<GenericResponse>;
        })
      );
  }

  /**
   * disable clients.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `disableClient$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  disableClient(
    params: {
      /**
       * The client's id that needs to be soft deleted
       */
      id: number;
    },
    context?: HttpContext
  ): Observable<GenericResponse> {
    return this.disableClient$Response(params, context).pipe(
      map((r: StrictHttpResponse<GenericResponse>): GenericResponse => r.body)
    );
  }

  /** Path part for operation `updateClientById()` */
  static readonly UpdateClientByIdPath = '/api/v1/clients/{id}';

  /**
   * Update client by id.
   *
   *
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateClientById()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientById$Response(
    params: {
      /**
       * The client's id that needs to be updated
       */
      id: number;
      body: UpdateClientRequest;
    },
    context?: HttpContext
  ): Observable<StrictHttpResponse<ClientResponse>> {
    const rb = new RequestBuilder(
      this.rootUrl,
      ClientService.UpdateClientByIdPath,
      'patch'
    );
    if (params) {
      rb.path('id', params.id, {});
      rb.body(params.body, 'application/json');
    }

    return this.http
      .request(
        rb.build({ responseType: 'json', accept: 'application/json', context })
      )
      .pipe(
        filter((r: any): r is HttpResponse<any> => r instanceof HttpResponse),
        map((r: HttpResponse<any>) => {
          return r as StrictHttpResponse<ClientResponse>;
        })
      );
  }

  /**
   * Update client by id.
   *
   *
   *
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateClientById$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateClientById(
    params: {
      /**
       * The client's id that needs to be updated
       */
      id: number;
      body: UpdateClientRequest;
    },
    context?: HttpContext
  ): Observable<ClientResponse> {
    return this.updateClientById$Response(params, context).pipe(
      map((r: StrictHttpResponse<ClientResponse>): ClientResponse => r.body)
    );
  }
}
